import { shadowFundIdByFamilyId } from '@foresightdata/metrics-calculator';
import { atom } from 'jotai';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { fundsAtom, selectableFundsAtom } from '../../../services/state/AppConfigStateJ';
import { SelectItem } from '../../../types';
import { compareBySortOrderThenName } from '../../../util/comparators';
import { NoGroupFundLabel } from '@foresightdata/web-common-core';
import { selectedViewPFJ } from './ViewStateJ';

export const checkedFundsStateJ = atom<IFundDataModel[] | null>(null);

// FD-2976: kept non-`async` so re-derivations don't yield a fresh Promise on
// every `viewsPFJ` update. An `async` atom would force consumers
// (FundFilterComponent reads both of these) to re-suspend the surrounding
// Suspense boundary on every view write, flashing the table area.
export const includeNonParticipatingRoundsStateJ = atom((get): boolean | Promise<boolean> => {
  const view = get(selectedViewPFJ);
  if (view instanceof Promise) {
    return view.then((v) => v?.includeNonParticipatingRounds ?? true);
  }
  return view?.includeNonParticipatingRounds ?? true;
});

export const activeFundsStateJ = atom((get): IFundDataModel[] | null | Promise<IFundDataModel[] | null> => {
  const view = get(selectedViewPFJ);
  if (view instanceof Promise) {
    return view.then((v) => v?.filteredFunds ?? null);
  }
  return view?.filteredFunds ?? null;
});

/** Every fund, intermediaries included: Finance2's filters read these, where a blocker's
 * transactions are real transactions. The portfolio picker uses the `portfolio*` atoms below. */
export const areAllFundsSelectedStateJ = atom((get): boolean => {
  const checkedFunds = get(checkedFundsStateJ);
  const allFunds = get(fundsAtom);
  return !checkedFunds || checkedFunds.length === allFunds.length;
});

/** The portfolio picker's own set: reporting funds only (vehicles are not reported on). */
export const portfolioAllFundsSelectedStateJ = atom((get): boolean => {
  const checkedFunds = get(checkedFundsStateJ);
  const allFunds = get(selectableFundsAtom);
  return !checkedFunds || checkedFunds.length === allFunds.length;
});

export const areNoFundsSelectedStateJ = atom((get): boolean => {
  const checkedFunds = get(checkedFundsStateJ);
  return Boolean(checkedFunds) && checkedFunds?.length === 0;
});

/** See `areAllFundsSelectedStateJ` — every fund, for the Finance2 filters. */
export const fundOptionsStateJ = atom((get): SelectItem[] => {
  const allFunds = get(fundsAtom);
  const groupOf = createFundGroupResolver(allFunds);
  return sortGroupedFunds(allFunds, groupOf).map((fund) => fundToSelectItem(fund, groupOf));
});

/** The portfolio fund picker's options: reporting funds only. */
export const portfolioFundOptionsStateJ = atom((get): SelectItem[] => {
  const groupOf = createFundGroupResolver(get(fundsAtom));
  return sortGroupedFunds(get(selectableFundsAtom), groupOf).map((fund) => fundToSelectItem(fund, groupOf));
});

export const selectedFundsAsOptionsPFStateJ = atom((get): SelectItem[] => {
  const checkedFunds = get(checkedFundsStateJ);
  const options = get(portfolioFundOptionsStateJ);
  const groupOf = createFundGroupResolver(get(fundsAtom));
  return checkedFunds?.map((fund) => fundToSelectItem(fund, groupOf)) ?? options;
});

export type FundGroupResolver = (fund: IFundDataModel) => string;

const fundTypeGroup: FundGroupResolver = (fund) => fund.fundType || NoGroupFundLabel;

/**
 * Fund pickers group their options by fund family once the tenant has any (funds outside a family
 * fall under "Other"); tenants without families keep the fund-type grouping.
 */
export function createFundGroupResolver(allFunds: IFundDataModel[]): FundGroupResolver {
  const fundsById = new Map(allFunds.map((fund) => [fund.id, fund]));
  const shadowFundByFamily = shadowFundIdByFamilyId(fundsById);
  if (shadowFundByFamily.size === 0) {
    return fundTypeGroup;
  }
  return (fund) => {
    const shadowFundId = fund.familyId != null ? shadowFundByFamily.get(fund.familyId) : undefined;
    return (shadowFundId != null ? fundsById.get(shadowFundId)?.name : undefined) ?? NoGroupFundLabel;
  };
}

export function sortGroupedFunds(funds: IFundDataModel[], groupOf: FundGroupResolver = fundTypeGroup) {
  return [...funds].sort(compareFundsByGroup(groupOf));
}

export function fundToSelectItem(fund: IFundDataModel, groupOf?: FundGroupResolver): SelectItem {
  return { id: fund.id, value: fund.name, group: groupOf ? groupOf(fund) : fund.fundType };
}

export function compareFundsByGroup(groupOf: FundGroupResolver) {
  return (a: IFundDataModel, b: IFundDataModel) => {
    const aGroup = groupOf(a);
    const bGroup = groupOf(b);

    if (aGroup === bGroup) {
      return compareBySortOrderThenName(a, b);
    }
    if (aGroup === NoGroupFundLabel) {
      return 1;
    }
    if (bGroup === NoGroupFundLabel) {
      return -1;
    }
    // Groups have no sort order of their own, so they sort by name.
    return aGroup.localeCompare(bGroup, 'en', { sensitivity: 'base' });
  };
}

export const fundComparator = compareFundsByGroup(fundTypeGroup);
